import { useState } from "react";
import { useMutation } from "@apollo/client";
import { showSnackbar } from "@/lib/snackbarUtils";
import { UPDATE_EVENT_REGISTRATION_MUTATION } from "../../../queries";
import Button from "@/components/Button";
import useModal from "@/hooks/useModal";
import DateSelect from "@/components/DateSelect";
import { Controller, useForm } from "react-hook-form";
import { useEffect } from "react";
import { ApolloError } from "@apollo/client";
import Banner from "@/components/Banner";
import { DateTime } from "luxon";
import { Event } from "../../../types";
import { getDateFromIsoForZone } from "../_utils";
import { useParams } from "react-router-dom";
import useAmplitude from "@/hooks/useAmplitude";
import { EVENT_ANALYTICS_EVENTS, getEventAnalyticsData } from "@/pages/events/_utils/amplitude";

interface EditRegistrationModalProps {
  event: Event | undefined;
  eventRegistrationId: string;
  selectedRegistration: any;
  onClosed: () => void;
  refetch?: () => void;
}

const defaultValues = {
  registrationDate: {
    startDate: DateTime.now().toFormat("yyyy-MM-dd"),
    endDate: DateTime.now().toFormat("yyyy-MM-dd")
  }
};

type FieldValues = typeof defaultValues;

export default function EditRegistrationModal({
  event,
  eventRegistrationId,
  selectedRegistration,
  onClosed,
  refetch
}: EditRegistrationModalProps) {
  const { close, Modal } = useModal();
  const [bannerError, setBannerError] = useState("");
  const { eventSlug = "" } = useParams();
  const { trackEvent } = useAmplitude();

  const { control, handleSubmit, setValue } = useForm<FieldValues>({
    defaultValues,
    mode: "onChange"
  });

  const [updateEventRegistration, { loading: mutationLoading }] = useMutation(UPDATE_EVENT_REGISTRATION_MUTATION);

  useEffect(() => {
    if (selectedRegistration?.registeredAt) {
      const date = selectedRegistration.registeredAt;
      setValue("registrationDate", {
        startDate: date,
        endDate: date
      });
    }
  }, [selectedRegistration, setValue]);

  const handleUpdate = handleSubmit(async (fields: FieldValues) => {
    try {
      const { data } = await updateEventRegistration({
        variables: {
          input: {
            registrationId: eventRegistrationId,
            registeredAt: DateTime.fromISO(fields.registrationDate.startDate).toISO()
          }
        }
      });

      const { success, message } = data.updateEventRegistration;
      if (success) {
        refetch && refetch();
        showSnackbar("Registration updated successfully", { close: true });
        onClosed();
        trackEvent(
          EVENT_ANALYTICS_EVENTS.registrationUpdated,
          getEventAnalyticsData({
            source: "registrations",
            eventId: event?.id,
            eventSlug,
            registrationId: eventRegistrationId,
            action: "update"
          })
        );
      } else {
        setBannerError(message);
      }
    } catch (e) {
      setBannerError(`Error: ${(e as ApolloError).message}`);
    } finally {
      await close();
    }
  });

  const action = (
    <Button disabled={mutationLoading} onClick={handleUpdate}>
      Save
    </Button>
  );

  const minDate = getDateFromIsoForZone(event?.registrationOpenAt || "", event?.timezone);
  const maxDate = getDateFromIsoForZone(event?.registrationCloseAt || "", event?.timezone);

  return (
    <Modal
      action={action}
      onClosed={onClosed}
      headline="Edit Registration"
      data-testid="edit-modal"
      displayOverflow
      className="w-[490px]"
    >
      {!!bannerError && (
        <div className="mb-6">
          <Banner inline variant="error" description="The registration could not be updated." subText={bannerError} />
        </div>
      )}
      <input className="absolute opacity-0" />
      <Controller
        name="registrationDate"
        control={control}
        rules={{
          validate: (value) => {
            if (!value) {
              return `Registration date is required`;
            }
            if (maxDate && minDate) {
              const selectedDate = DateTime.fromISO(value.startDate);
              const maxDateTime = DateTime.fromJSDate(new Date(maxDate));
              const minDateTime = DateTime.fromJSDate(new Date(minDate));

              if (selectedDate < minDateTime || selectedDate > maxDateTime) {
                return `Registration date must be between ${minDateTime.toFormat(
                  "MM/dd/yyyy"
                )} and ${maxDateTime.toFormat("MM/dd/yyyy")}`;
              }
            }
            return true;
          }
        }}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <DateSelect
            label="Registration Date"
            startFrom={value.startDate ? new Date(value.startDate) : undefined}
            value={value}
            onChange={onChange}
            useSingleDate
            showFooter={false}
            error={!!error}
            errorMessage={error?.message}
            minDate={minDate}
            maxDate={maxDate}
          />
        )}
      />
    </Modal>
  );
}
